// Push to Amplemarket.
//
// Everything else on this page reads. This is the one control that writes to another system,
// and what it writes is named humans into a live email sequence, so four things are true here
// that are not true of anything else:
//
// * The browser never sends an email address. It names an account and the contacts on it, and
//   the server reads the addresses out of the book payload it already built.
// * Personalisation is sent, not inferred. The hint below says which template variables the
//   copy expects and who might be held back -- but it never blocks a checkbox, because
//   `dynamic_fields` cannot tell a required variable from one behind a fallback.
// * The "Amplemarket: <sequence>" tag beside a contact is a Salesforce mirror and can be hours
//   stale. It is a hint, never the gate; the server re-checks the exclusion list at write time.
// * "Already in this sequence" is an ordinary outcome, not an error. The endpoint is add-only,
//   so the panel says so plainly and in the same voice as a success.
//
// Nothing is preselected and nothing is implied: the button names how many people it is about
// to push and the panel lists them, because the difference between pushing one person and eight
// should never be something you find out from the results.

import { useEffect, useRef, useState } from 'react';
import { PREVIEW_DRY_RUN_MESSAGE, fetchLeadLists, fetchSequences, isPreviewDryRun, panelMessage, pushToAmplemarket } from '../api/client';
import type { TokenGetter } from '../api/client';
import { contactDisplayName } from '../lib/contactName';
import { OutreachContext } from './OutreachContext';
import type { BookAccount, Contact, PushOption, PushResult } from '../api/types';
import { SUPPLIABLE_FIELDS, unsatisfiedFields } from './permissions';

// How each outcome reads. `already_enrolled` and `withheld` are deliberately toneless: both are
// true, useful answers, and colouring them as failures would train people to push again.
const OUTCOME_LABEL: Record<string, string> = {
  added: 'added',
  already_enrolled: 'already enrolled',
  skipped: 'not added',
  queued: 'queued',
  unconfirmed: 'unconfirmed',
  withheld: 'withheld',
};

type TargetKind = 'sequence' | 'lead_list';

interface Outcome { tone: string; message: string; result: PushResult | null }

// The headline above the per-contact rows. Green only when every selected contact was actually
// added -- a batch where half were skipped is not a success with a footnote -- and amber as
// soon as anybody was skipped, so "nobody was contacted" never wears the same neutral box as
// "everybody was already enrolled".
function outcomeFromResult(result: PushResult): Outcome {
  const rows = result.results ?? [];
  const counts: Record<string, number> = {};
  rows.forEach(row => { counts[row.outcome] = (counts[row.outcome] ?? 0) + 1; });
  const target = result.target?.name ?? 'the target';

  if (result.queued) {
    return {
      tone: '',
      result,
      message: `${rows.length === 1 ? '1 contact' : rows.length + ' contacts'} queued for `
        + `“${target}”. The list enriches and validates afterwards, so nothing is confirmed yet.`,
    };
  }
  const everyoneAdded = counts.added === rows.length;
  const summary = Object.keys(OUTCOME_LABEL)
    .filter(outcome => counts[outcome])
    .map(outcome => `${counts[outcome]} ${OUTCOME_LABEL[outcome]}`)
    .join(' · ');

  // A skip is the one outcome here where somebody expected to be contacted was not, so the
  // headline carries it rather than leaving it to be spotted a row at a time. Every other mix
  // -- already enrolled, withheld, queued -- keeps the neutral box it has always had.
  let tone = '';
  if (everyoneAdded) tone = 'added';
  else if (counts.skipped) tone = 'warn';
  return { tone, result, message: `“${target}” — ${summary}.` };
}

export function PushPanel({
  row,
  selected,
  getToken,
  onClose,
}: {
  row: BookAccount;
  selected: Contact[];
  getToken: TokenGetter;
  onClose: () => void;
}) {
  const [kind, setKind] = useState<TargetKind>('sequence');
  const [query, setQuery] = useState('');
  const [options, setOptions] = useState<PushOption[]>([]);
  const [truncated, setTruncated] = useState(false);
  const [targetId, setTargetId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [outcome, setOutcome] = useState<Outcome | null>(null);
  const [pushing, setPushing] = useState(false);
  const searchRef = useRef<HTMLInputElement>(null);

  useEffect(() => { searchRef.current?.focus(); }, []);

  // Debounced search, re-run whenever the kind or the query changes. The abort flag is what
  // stops a slow response for "vox" landing after a faster one for "voxel".
  //
  // `getToken` is deliberately not a dependency. Re-running this clears the chosen target, and
  // the only thing that should clear somebody's chosen sequence is them changing what they are
  // searching for -- not the session token being refreshed underneath them. The getter is a
  // stable identity out of AuthContext, and it reads the current token when called either way.
  useEffect(() => {
    let abandoned = false;
    setLoading(true);
    const timer = setTimeout(async () => {
      try {
        let loaded: PushOption[];
        let wasTruncated = false;
        if (kind === 'sequence') {
          const payload = await fetchSequences(getToken, query);
          wasTruncated = Boolean(payload.truncated);
          loaded = payload.sequences.map(sequence => ({
            id: sequence.id,
            label: `${sequence.name}  ·  ${sequence.status}`,
            fields: sequence.dynamic_fields,
          }));
        } else {
          const payload = await fetchLeadLists(getToken, query);
          wasTruncated = Boolean(payload.truncated);
          loaded = payload.lead_lists.map(list => ({
            id: list.id,
            label: `${list.name}  ·  ${list.owner || 'no owner'}`,
            owner: list.owner,
          }));
        }
        if (abandoned) return;
        setOptions(loaded);
        setTruncated(wasTruncated);
        setTargetId(null);
      } catch (error) {
        if (abandoned) return;
        setOptions([]);
        setTargetId(null);
        setOutcome({ tone: 'bad', result: null,
          message: panelMessage(error, 'Could not load targets.') });
      } finally {
        if (!abandoned) setLoading(false);
      }
    }, query ? 250 : 0);

    return () => { abandoned = true; clearTimeout(timer); };
  }, [kind, query]);

  const chosen = options.find(option => option.id === targetId) ?? null;

  // What the chosen target will do with the push, said before the button is pressed rather than
  // after: which template variables the copy expects, and whose credits a list spends.
  const hint = (): string => {
    if (!chosen) {
      // A capped search is not an answer. Say so, or forty of 1,500 sequences reads as all of
      // them and the one somebody wants looks like it does not exist.
      return truncated ? 'Showing the first matches only — narrow the search by name.' : '';
    }
    if (kind === 'lead_list') {
      return `Enrichment and validation follow the list's own settings, and any credits are `
        + `billed to ${chosen.owner || 'the list owner'}. Contacts are sent by address where `
        + `there is one and by LinkedIn URL otherwise, which is two requests.`;
    }
    const fields = chosen.fields ?? [];
    if (!fields.length) return 'This sequence uses no template variables.';

    // What the dashboard actually sends, split from what only Amplemarket can fill in. This is
    // the correction that matters: the wording here once claimed Amplemarket filled these from
    // its own data. It does not -- the sent `data` is the only source, and its absence is what
    // made an early push fail.
    const supplied = fields.filter(name => SUPPLIABLE_FIELDS.includes(name));
    const unsupplied = fields.filter(name => !SUPPLIABLE_FIELDS.includes(name));
    const parts: string[] = [];
    if (supplied.length) {
      parts.push(`This sequence personalises on ${supplied.join(', ')}, sent from Salesforce.`);
    }
    if (unsupplied.length) {
      parts.push(`It also uses ${unsupplied.join(', ')}, which the book has no source for — `
        + 'Amplemarket has to fill that in or the push is refused.');
    }
    // Who this sequence in particular holds back, named before the button rather than after.
    const held = selected.filter(
      contact => unsatisfiedFields(contact, row, chosen.fields).length,
    );
    if (held.length) {
      parts.push(`${held.length === 1 ? '1 selected contact has' : held.length
        + ' selected contacts have'} none on record; if this sequence needs it rather than just `
        + 'using it, they are held back and the rest still go.');
    }
    return parts.join(' ');
  };

  const runPush = async () => {
    if (!selected.length || !targetId || !chosen) return;
    setPushing(true);
    setOutcome({ tone: '', result: null,
      message: `Pushing ${selected.length === 1 ? '1 contact' : selected.length + ' contacts'}…` });
    try {
      const result = await pushToAmplemarket(getToken, {
        target_type: kind,
        target_id: targetId,
        target_name: (chosen.label || '').split('  ·  ')[0] ?? '',
        account_id: row.id,
        contact_ids: selected.map(contact => contact.id),
      });
      setOutcome(outcomeFromResult(result));
    } catch (error) {
      // A preview's refusal is the expected dry run: neutral, never "The push failed".
      setOutcome(isPreviewDryRun(error)
        ? { tone: '', result: null, message: PREVIEW_DRY_RUN_MESSAGE }
        : { tone: 'bad', result: null, message: panelMessage(error, 'The push failed.') });
    } finally {
      setPushing(false);
    }
  };

  const contactCount = `${selected.length} contact${selected.length === 1 ? '' : 's'}`;
  const goLabel = kind === 'sequence' ? `Enroll ${contactCount}` : `Add ${contactCount} to list`;

  return (
    <div className="pushpanel">
      {/* Who is about to be pushed, by name. A count alone would let eight people ride on a
          number nobody read. */}
      <p className="pushnote">
        <b>{`Review ${contactCount}`}:</b>{' '}
        {selected.map(contact => contactDisplayName(contact.name, contact.email, 'this contact')).join(', ')}
      </p>

      <div className="pushrow">
        <label className="pushnote" htmlFor="pushkind">Destination</label>
        <select
          id="pushkind"
          value={kind}
          onChange={event => {
            setKind(event.target.value as TargetKind);
            setOptions([]);
            setTargetId(null);
            setOutcome(null);
          }}
        >
          <option value="sequence">a sequence</option>
          <option value="lead_list">a lead list</option>
        </select>
        <input
          type="search"
          ref={searchRef}
          placeholder="Search by name"
          aria-label="Search sequences and lead lists by name"
          value={query}
          autoComplete="off"
          onChange={event => setQuery(event.target.value)}
        />
      </div>

      <div className="pushrow">
        {/* The first option is an unselectable placeholder, so nothing is chosen until somebody
            chooses it. A write control that arrives with a target already filled in is one
            stray click away from putting people into whichever sequence happened to be edited
            most recently, which is what this list is ordered by. */}
        <select
          className="target"
          aria-label="Target"
          value={targetId ?? ''}
          onChange={event => setTargetId(event.target.value || null)}
        >
          {loading ? (
            <option value="">Searching…</option>
          ) : !options.length ? (
            <option value="">{query ? 'Nothing matches that name' : 'Nothing to push into'}</option>
          ) : (
            <>
              <option value="">
                {kind === 'sequence' ? 'Choose a sequence…' : 'Choose a lead list…'}
              </option>
              {options.map(option => (
                <option key={option.id} value={option.id}>{option.label}</option>
              ))}
            </>
          )}
        </select>
      </div>

      <OutreachContext row={row} />
      {chosen ? <p className="pushdestination"><b>Destination:</b> {chosen.label}</p> : null}
      <p className="pushconsequence">
        {kind === 'sequence'
          ? 'Adds these contacts to the selected sequence. Active sequences may begin outreach according to their schedule.'
          : 'Adds these contacts to the selected lead list. This action is not sequence enrollment.'}
      </p>
      <div className="pushrow">
        <button
          className="btn primary"
          type="button"
          onClick={runPush}
          disabled={!chosen || !selected.length || pushing}
        >
          {goLabel}
        </button>
        <button className="btn" type="button" onClick={onClose}>Cancel</button>
        <span className="pushnote">{hint()}</span>
      </div>

      <div role="status" aria-live="polite">
        {outcome ? (
          <div className={'pushresult ' + outcome.tone}>
            {outcome.message}
            {outcome.result?.results?.length ? (
              <div className="pushrows">
                {outcome.result.results.map((entry, index) => (
                  <div className="pushrowresult" key={index}>
                    <span className="pushwho">
                      {entry.name || entry.identifier || 'contact'}
                    </span>
                    <span className={'pushoutcome ' + entry.outcome}>
                      {OUTCOME_LABEL[entry.outcome] ?? entry.outcome}
                    </span>
                    {entry.detail ? <span className="pushdetail">{entry.detail}</span> : null}
                  </div>
                ))}
              </div>
            ) : null}
          </div>
        ) : null}
      </div>

      <p className="pushnote" style={{ marginTop: 8 }}>
        {kind === 'sequence'
          ? 'Only active and draft sequences are listed. No Amplemarket override is sent; excluded contacts are skipped. Contacts missing a required template variable are held back.'
          : 'The lead list enriches and validates contacts after they are added. A queued result is not confirmation that a contact was accepted.'}
        {' '}The “Amplemarket: …” tag comes from Salesforce and can be hours behind,
        so it will not change here straight away.
      </p>
    </div>
  );
}

export { OUTCOME_LABEL };
